import React, { useEffect, useMemo, useState } from 'react';
import {
  BellRing,
  Bot,
  CheckCircle2,
  ChevronRight,
  CreditCard,
  Download,
  ExternalLink,
  FileText,
  Filter,
  Mail,
  Pause,
  Play,
  Plus,
  Radar,
  Rss,
  Search,
  Settings2,
  Sparkles,
  Trash2,
  Webhook,
} from 'lucide-react';
import { api, formatDate, money, sourceLabel } from './api.js';
import {
  Header,
  Footer,
  Modal,
  SourceGlyph,
  Spinner,
  StatusPill,
  Toggle,
  useToast,
} from './components.jsx';

const emptyPreferences = {
  sources: [],
  notifications: { emailEnabled: true, emailMode: 'instant', discordEnabled: false },
  discord: { connected: false },
  billing: { plan: 'free', subscriptionStatus: 'none' },
};

export function Dashboard() {
  const [config, setConfig] = useState(null);
  const [user, setUser] = useState(undefined);
  const [preferences, setPreferences] = useState(emptyPreferences);
  const [games, setGames] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [tab, setTab] = useState(new URLSearchParams(window.location.search).get('plan') === 'crawler' ? 'billing' : 'web');
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const { showToast, Toast } = useToast();

  async function loadDashboard() {
    const [dashboard, nextPreferences] = await Promise.all([api('/api/games'), api('/api/preferences')]);
    setGames(Array.isArray(dashboard.games) ? dashboard.games : []);
    setMentions(Array.isArray(dashboard.mentions) ? dashboard.mentions : []);
    setPreferences(nextPreferences);
  }

  useEffect(() => {
    Promise.all([api('/api/config'), api('/api/me')])
      .then(async ([nextConfig, me]) => {
        setConfig(nextConfig);
        setUser(me.user);
        if (me.user) await loadDashboard();
      })
      .catch((error) => showToast(error.message, true))
      .finally(() => setLoading(false));
  }, []);

  async function logout() {
    await api('/api/auth/logout', { method: 'POST' }).catch(() => {});
    setUser(null);
    setGames([]);
    setMentions([]);
    setTab('web');
  }

  if (loading || !config) return <PageLoading />;
  if (!user) return <AuthScreen config={config} onLogin={async (nextUser) => { setUser(nextUser); await loadDashboard(); }} showToast={showToast} Toast={Toast} />;

  const tabs = [
    ['web', Radar, 'Web'],
    ['sources', Search, 'Sources'],
    ['delivery', BellRing, 'Delivery'],
    ['billing', CreditCard, 'Billing'],
  ];

  return (
    <div className="app-page">
      <Header user={user} onLogout={logout} />
      <div className="app-shell shell">
        <aside className="app-sidebar">
          <div className="account-chip"><span>{user.email.slice(0, 1).toUpperCase()}</span><div><strong>{user.email}</strong><small>{preferences.billing.plan === 'scout' ? 'Crawler plan' : 'Free watch'}</small></div></div>
          <nav aria-label="Dashboard">
            {tabs.map(([key, Icon, label]) => <button key={key} className={tab === key ? 'active' : ''} onClick={() => setTab(key)}><Icon size={18} />{label}</button>)}
          </nav>
          {user.isAdmin && <a className="sidebar-admin" href="/admin"><Settings2 size={17} />Admin dashboard<ChevronRight size={15} /></a>}
        </aside>

        <main className="app-main">
          {tab === 'web' && (
            <WebDashboard
              games={games}
              mentions={mentions}
              preferences={preferences}
              config={config}
              onAdd={() => setShowAdd(true)}
              onReload={loadDashboard}
              showToast={showToast}
            />
          )}
          {tab === 'sources' && <Sources preferences={preferences} setPreferences={setPreferences} config={config} showToast={showToast} />}
          {tab === 'delivery' && <Delivery preferences={preferences} setPreferences={setPreferences} config={config} showToast={showToast} />}
          {tab === 'billing' && <Billing user={user} preferences={preferences} config={config} showToast={showToast} />}
        </main>
      </div>
      <MobileTabs tabs={tabs} tab={tab} setTab={setTab} />
      <AddWatchModal open={showAdd} onClose={() => setShowAdd(false)} onCreated={(game) => {
        setGames((current) => [{ ...game, sources: ['press'], stores: [], mention_count: 0 }, ...current.filter((item) => item.id !== game.id)]);
        setShowAdd(false);
        loadDashboard().catch(() => showToast('Watch added. Refresh the page if its latest crawl does not appear yet.', true));
      }} showToast={showToast} />
      <Toast />
    </div>
  );
}

function PageLoading() {
  return <div className="full-loading"><Spinner label="Following the threads…" /></div>;
}

function AuthScreen({ config, onLogin, showToast, Toast }) {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [developmentCode, setDevelopmentCode] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  async function requestCode(event) {
    event.preventDefault();
    setBusy(true);
    try {
      const result = await api('/api/auth/request-code', { method: 'POST', body: JSON.stringify({ email, marketingConsent }) });
      setDevelopmentCode(result.debugCode || '');
      setStep('code');
      showToast(result.debugCode ? 'Local development code generated.' : 'Six-digit code sent.');
    } catch (error) { showToast(error.message, true); }
    finally { setBusy(false); }
  }

  async function verifyCode(event) {
    event.preventDefault();
    setBusy(true);
    try {
      const result = await api('/api/auth/verify-code', { method: 'POST', body: JSON.stringify({ email, code }) });
      await onLogin(result.user);
    } catch (error) { showToast(error.message, true); }
    finally { setBusy(false); }
  }

  return (
    <div className="auth-page">
      <Header />
      <main className="shell auth-layout">
        <section className="auth-pitch">
          <span className="eyebrow"><Radar size={15} /> Your Web</span>
          <h1 className="auth-headline"><span>One Login.</span><em>Catch Every Mention.</em></h1>
          <p>Sign in without a password. Web monitoring for one game is free; upgrade to Crawler or a Quick Catch at any time.</p>
          <div className="auth-mini-grid">
            <div><SourceGlyph source="press" /><span><strong>Web</strong><small>Included</small></span></div>
            <div><Mail size={19} /><span><strong>Email</strong><small>Optional</small></span></div>
            <div><Bot size={19} /><span><strong>Discord</strong><small>One-click bot</small></span></div>
          </div>
        </section>
        <section className="auth-card">
          {step === 'email' ? (
            <form onSubmit={requestCode}>
              <span className="step-kicker">Step 1 of 2</span>
              <h2>Enter your email</h2>
              <p>We’ll send a code that expires in {config.loginCodeTtlMinutes || 10} minutes.</p>
              <label>Email address<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required placeholder="you@studio.com" /></label>
              <label className="check-row"><input type="checkbox" checked={marketingConsent} onChange={(event) => setMarketingConsent(event.target.checked)} /><span>Tell me when Olamia AI adds useful tools. Optional.</span></label>
              <button className="button button-wide" disabled={busy}>{busy ? 'Sending…' : 'Send login code'}<ChevronRight size={17} /></button>
            </form>
          ) : (
            <form onSubmit={verifyCode}>
              <span className="step-kicker">Step 2 of 2</span>
              <h2>Check your inbox</h2>
              <p>Code sent to <strong>{email}</strong>.</p>
              {developmentCode && <div className="development-code" role="status"><span>Local development code</span><strong>{developmentCode}</strong><small>This remains visible here and is never shown in production.</small></div>}
              <label>Six-digit code<input className="code-input" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength="6" value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} required placeholder="000000" /></label>
              <button className="button button-wide" disabled={busy}>{busy ? 'Checking…' : 'Open my web'}<ChevronRight size={17} /></button>
              <button type="button" className="text-button auth-back" onClick={() => { setStep('email'); setCode(''); setDevelopmentCode(''); }}>Use a different email</button>
            </form>
          )}
          <small className="auth-legal">By continuing, you agree to the <a href="/terms">terms</a> and acknowledge the <a href="/privacy">privacy notice</a>.</small>
        </section>
      </main>
      <Footer />
      <Toast />
    </div>
  );
}

function WebDashboard({ games, mentions, preferences, config, onAdd, onReload, showToast }) {
  const [sourceFilter, setSourceFilter] = useState('all');
  const filtered = sourceFilter === 'all' ? mentions : mentions.filter((mention) => mention.source_type === sourceFilter);
  const activeSources = preferences.sources.filter((source) => source.effective).length;
  const recentCount = mentions.filter((mention) => Date.now() - new Date(mention.discovered_at).getTime() < 7 * 86400000).length;

  return (
    <>
      <div className="app-title-row"><div><span className="eyebrow">Your Web</span><h1>What the spider caught.</h1><p>Every stored mention, newest first.</p></div><button className="button" onClick={onAdd}><Plus size={17} />Add a watch</button></div>
      <div className="stat-grid">
        <Stat value={games.length} label="games watched" />
        <Stat value={activeSources} label="active sources" />
        <Stat value={mentions.length} label="mentions stored" />
        <Stat value={recentCount} label="caught this week" accent />
      </div>
      <section className="dashboard-card">
        <div className="card-head"><div><h2>Active watches</h2><p>Free watches crawl daily. Crawler and priority watches run more often.</p></div><StatusPill tone="green">{games.filter((game) => game.active).length} crawling</StatusPill></div>
        <div className="watch-list">
          {!games.length && <Empty title="No watches spun yet." body="Add your game and Press Spider will start with the Web source." action={<button className="button button-small" onClick={onAdd}>Add a watch</button>} />}
          {games.map((game) => <WatchRow key={game.id} game={game} config={config} billing={preferences.billing} onReload={onReload} showToast={showToast} />)}
        </div>
      </section>
      <section className="dashboard-card">
        <div className="card-head mentions-head">
          <div><h2>Mention trail</h2><p>Filter the trail or take the full CSV with you.</p></div>
          <div className="card-actions">
            <label className="select-with-icon"><Filter size={15} /><select value={sourceFilter} onChange={(event) => setSourceFilter(event.target.value)}><option value="all">All sources</option><option value="press">Web</option><option value="youtube">YouTube</option><option value="twitch">Twitch</option><option value="tiktok">TikTok</option></select></label>
            <a className="button button-quiet button-small" href="/api/account/mentions.csv"><Download size={16} />Download CSV</a>
          </div>
        </div>
        <div className="mention-list">
          {!filtered.length && <Empty title="No mentions yet." body="The first crawl can take a little while. You can leave this page. The watch keeps running." />}
          {filtered.map((mention) => <MentionRow key={mention.id} mention={mention} />)}
        </div>
      </section>
    </>
  );
}

function nextPressCrawl(game, billing) {
  if (!game.last_press_checked_at) return 'Queued now';
  const priorityActive = game.priority_until && new Date(game.priority_until).getTime() > Date.now();
  const intervalMinutes = priorityActive || billing?.plan === 'scout' ? 15 : 24 * 60;
  const dueAt = new Date(game.last_press_checked_at).getTime() + intervalMinutes * 60 * 1000;
  return dueAt <= Date.now() ? 'Queued for next run' : formatDate(new Date(dueAt).toISOString());
}

function WatchRow({ game, config, billing, onReload, showToast }) {
  const [busy, setBusy] = useState(false);
  async function changeState() {
    setBusy(true);
    try {
      await api(`/api/games/${game.id}/state`, { method: 'PATCH', body: JSON.stringify({ active: !game.active }) });
      await onReload();
    } catch (error) { showToast(error.message, true); }
    finally { setBusy(false); }
  }
  async function remove() {
    if (!window.confirm(`Remove the watch for ${game.title}? Its stored mentions remain available in your export.`)) return;
    setBusy(true);
    try {
      await api(`/api/games/${game.id}`, { method: 'DELETE' });
      await onReload();
    } catch (error) { showToast(error.message, true); }
    finally { setBusy(false); }
  }
  async function quickCatch() {
    setBusy(true);
    try {
      const currency = new URLSearchParams(window.location.search).get('currency') || 'USD';
      const result = await api('/api/billing/quick-catch/checkout', { method: 'POST', body: JSON.stringify({ monitorId: game.id, currency }) });
      window.location.assign(result.url);
    } catch (error) { showToast(error.message, true); setBusy(false); }
  }
  const sources = Array.isArray(game.sources) ? game.sources : ['press'];
  return (
    <article className="watch-row">
      <div className="watch-primary"><span className={`watch-status ${game.active ? 'active' : ''}`} /><div><h3>{game.title}</h3><div className="source-mini-list">{sources.map((source) => <span key={source}><SourceGlyph source={source} size={13} />{sourceLabel(source)}</span>)}</div></div></div>
      <div className="watch-meta"><span><strong>{game.mention_count || 0}</strong> mentions</span><span>Last crawl <strong>{formatDate(game.last_press_checked_at)}</strong></span><span>Next eligible crawl <strong>{nextPressCrawl(game, billing)}</strong></span></div>
      <div className="watch-actions">
        {config.billingEnabled && <button className="icon-button labelled" title="Run a one-off priority crawl" onClick={quickCatch} disabled={busy}><Sparkles size={16} /><span>Quick Catch</span></button>}
        <button className="icon-button" onClick={changeState} disabled={busy} aria-label={game.active ? 'Pause watch' : 'Resume watch'}>{game.active ? <Pause size={17} /> : <Play size={17} />}</button>
        <button className="icon-button danger" onClick={remove} disabled={busy} aria-label="Remove watch"><Trash2 size={17} /></button>
      </div>
    </article>
  );
}

function MentionRow({ mention }) {
  return (
    <article className="mention-row">
      <span className={`mention-source ${mention.source_type}`}><SourceGlyph source={mention.source_type} size={17} /></span>
      <div><div className="mention-byline"><span>{sourceLabel(mention.source_type)}</span>{mention.source_name && <><i />{mention.source_name}</>}</div><a href={mention.url} target="_blank" rel="noreferrer">{mention.title}<ExternalLink size={13} /></a><small>{mention.author ? `By ${mention.author}. ` : ''}{formatDate(mention.published_at || mention.discovered_at)}</small></div>
    </article>
  );
}

function Sources({ preferences, setPreferences, config, showToast }) {
  const [draft, setDraft] = useState(preferences.sources);
  const [busy, setBusy] = useState(false);
  useEffect(() => setDraft(preferences.sources), [preferences.sources]);
  const paidSelected = draft.filter((source) => !source.free && source.selected && source.status !== 'coming_soon').map((source) => source.key);

  function toggle(key, selected) {
    setDraft((sources) => sources.map((source) => source.key === key ? { ...source, selected } : source));
  }
  async function save() {
    setBusy(true);
    try {
      const result = await api('/api/preferences', {
        method: 'PATCH',
        body: JSON.stringify({ sources: draft.filter((source) => source.selected).map((source) => source.key) }),
      });
      setPreferences(result);
      showToast('Source preferences saved.');
    } catch (error) { showToast(error.message, true); }
    finally { setBusy(false); }
  }
  async function subscribe() {
    setBusy(true);
    try {
      const result = await api('/api/billing/subscription/checkout', { method: 'POST', body: JSON.stringify({ sources: paidSelected }) });
      window.location.assign(result.url);
    } catch (error) { showToast(error.message, true); setBusy(false); }
  }
  async function updateSubscription() {
    setBusy(true);
    try {
      const result = await api('/api/billing/subscription', { method: 'PATCH', body: JSON.stringify({ sources: paidSelected }) });
      setPreferences(result.preferences);
      showToast('Subscription add-ons updated. Stripe will apply any proration.');
    } catch (error) { showToast(error.message, true); }
    finally { setBusy(false); }
  }
  async function joinWaitlist(sourceKey) {
    try {
      await api(`/api/channels/${sourceKey}/interest`, { method: 'POST' });
      setDraft((sources) => sources.map((source) => source.key === sourceKey ? { ...source, interested: true } : source));
      showToast('You are on the TikTok source waitlist.');
    } catch (error) { showToast(error.message, true); }
  }
  return (
    <>
      <div className="app-title-row"><div><span className="eyebrow">Sources</span><h1>Choose where to crawl.</h1><p>Web stays free. Paid sources become active through your subscription or an admin grant.</p></div><button className="button" onClick={save} disabled={busy}>Save sources</button></div>
      <div className="source-settings-grid">
        {draft.map((source) => (
          <article className={`source-setting ${source.selected ? 'selected' : ''}`} key={source.key}>
            <div className={`source-setting-icon ${source.key}`}><SourceGlyph source={source.key} size={23} /></div>
            <div className="source-setting-head"><div><h2>{source.label}</h2><p>{source.description}</p></div>
              {source.status === 'coming_soon' ? <StatusPill>Waitlist</StatusPill> : source.free ? <StatusPill tone="green">Free</StatusPill> : source.effective ? <StatusPill tone="green">Active</StatusPill> : <StatusPill tone="orange">{money(source.priceMonthlyCents)}/mo</StatusPill>}
            </div>
            <div className="source-setting-foot">
              {source.status === 'coming_soon'
                ? <button className="button button-quiet button-small" onClick={() => joinWaitlist(source.key)} disabled={source.interested}>{source.interested ? 'On the waitlist' : 'Join waitlist'}</button>
                : <><span>{source.free ? 'Always part of your watch' : source.effective ? 'Crawling when selected' : 'Select now, unlock in Billing'}</span><Toggle checked={source.selected || source.free} disabled={source.free} onChange={(value) => toggle(source.key, value)} label={`Monitor ${source.label}`} /></>}
            </div>
          </article>
        ))}
      </div>
      {config.billingEnabled && preferences.billing.plan !== 'scout' && paidSelected.length > 0 && (
        <div className="inline-upsell"><div><Sparkles size={20} /><span><strong>Ready to activate {paidSelected.map(sourceLabel).join(' + ')}?</strong><small>Crawler includes YouTube and Twitch. Stripe shows the final recurring total before payment.</small></span></div><button className="button button-small" onClick={subscribe} disabled={busy}>Continue to Stripe</button></div>
      )}
      {config.billingEnabled && preferences.billing.plan === 'scout' && paidSelected.slice().sort().join(',') !== (preferences.billing.subscribedSources || []).slice().sort().join(',') && (
        <div className="inline-upsell"><div><CreditCard size={20} /><span><strong>Apply these source changes to Crawler?</strong><small>Stripe will calculate any proration before the next invoice.</small></span></div><button className="button button-small" onClick={updateSubscription} disabled={busy}>Update subscription</button></div>
      )}
      <RssSources paid={preferences.billing.plan === 'scout'} showToast={showToast} />
    </>
  );
}

function RssSources({ paid, showToast }) {
  const [feeds, setFeeds] = useState([]);
  const [canManage, setCanManage] = useState(paid);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  async function load() {
    const result = await api('/api/rss-feeds');
    setFeeds(result.feeds || []); setCanManage(result.canManage);
  }
  useEffect(() => { load().catch(() => {}); }, []);
  async function addUrl(feedUrl) {
    setBusy(true);
    try {
      const result = await api('/api/rss-feeds', { method: 'POST', body: JSON.stringify({ url: feedUrl }) });
      setUrl(''); await load(); showToast(result.message);
    } catch (error) { showToast(error.message, true); }
    finally { setBusy(false); }
  }
  async function add(event) {
    event.preventDefault();
    await addUrl(url);
  }
  async function remove(id) {
    setBusy(true);
    try { await api(`/api/rss-feeds/${id}`, { method: 'DELETE' }); await load(); showToast('RSS feed removed from your web.'); }
    catch (error) { showToast(error.message, true); }
    finally { setBusy(false); }
  }
  return <section className="dashboard-card rss-source-card">
    <div className="card-head"><div><h2>Custom RSS sources</h2><p>Add unlimited public RSS or Atom feeds. Shared feeds become available to other Crawler accounts.</p></div><Rss size={21} /></div>
    <div className="rss-source-body">
      {!canManage && <div className="integration-note">Custom RSS feeds are included with Crawler and active Quick Catch access.</div>}
      {canManage && <form className="rss-add-form" onSubmit={add}><label>RSS or Atom feed URL<input type="url" required value={url} onChange={(event) => setUrl(event.target.value)} placeholder="https://example.com/feed.xml" /></label><button className="button" disabled={busy}>{busy ? 'Checking feed…' : 'Check and add feed'}</button></form>}
      <div className="rss-feed-list">{feeds.map((feed) => <div key={feed.id}><span><Rss size={16} /><span><strong>{feed.title}</strong><small>{feed.feed_url}</small></span></span>{feed.selected ? <button className="text-button" onClick={() => remove(feed.id)} disabled={busy}>Remove</button> : canManage ? <button className="text-button" onClick={() => addUrl(feed.feed_url)} disabled={busy}>Add</button> : <StatusPill>Available</StatusPill>}</div>)}{!feeds.length && <p>No custom feeds have been added yet.</p>}</div>
    </div>
  </section>;
}

function Delivery({ preferences, setPreferences, config, showToast }) {
  const [draft, setDraft] = useState(preferences.notifications);
  const [channels, setChannels] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => setDraft(preferences.notifications), [preferences.notifications]);
  useEffect(() => {
    if (preferences.discord.connected) api('/api/integrations/discord/channels').then((result) => setChannels(result.channels || [])).catch(() => {});
  }, [preferences.discord.connected]);

  async function save() {
    setBusy(true);
    try {
      const result = await api('/api/preferences', { method: 'PATCH', body: JSON.stringify(draft) });
      setPreferences(result);
      showToast('Delivery preferences saved.');
    } catch (error) { showToast(error.message, true); }
    finally { setBusy(false); }
  }
  async function connectDiscord() {
    setBusy(true);
    try {
      const result = await api('/api/integrations/discord/start', { method: 'POST' });
      window.location.assign(result.url);
    } catch (error) { showToast(error.message, true); setBusy(false); }
  }
  async function selectChannel(channelId) {
    setBusy(true);
    try {
      const result = await api('/api/integrations/discord/channel', { method: 'POST', body: JSON.stringify({ channelId }) });
      setPreferences((value) => ({ ...value, discord: result.discord }));
      setDraft((value) => ({ ...value, discordEnabled: true }));
      showToast('Discord channel connected.');
    } catch (error) { showToast(error.message, true); }
    finally { setBusy(false); }
  }

  return (
    <>
      <div className="app-title-row"><div><span className="eyebrow">Delivery</span><h1>Choose where mentions land.</h1><p>Your Web is always here. Add email or install the Olamia AI bot for Discord.</p></div><button className="button" onClick={save} disabled={busy}>Save delivery</button></div>
      {preferences.billing.plan !== 'scout' && <div className="integration-note large">Email and Discord delivery switch on with Crawler, or for the duration of a paid Quick Catch. You can set them up now without being charged.</div>}
      <div className="delivery-grid">
        <article className="delivery-card">
          <div className="delivery-card-top"><span className="delivery-big-icon"><Mail /></span><div><h2>Email alerts</h2><p>Send new mentions to your login email.</p></div><Toggle checked={draft.emailEnabled} onChange={(value) => setDraft({ ...draft, emailEnabled: value })} label="Email alerts" /></div>
          <div className="integration-note">Delivery cadence: as each new mention is caught.</div>
        </article>
        <article className="delivery-card">
          <div className="delivery-card-top"><span className="delivery-big-icon discord"><Bot /></span><div><h2>Olamia AI for Discord</h2><p>Install once, choose a channel, and let the bot post mentions.</p></div>{preferences.discord.connected && <CheckCircle2 className="connected-check" />}</div>
          {!config.discordBotEnabled && <div className="integration-note">Bot credentials are not configured on this environment yet.</div>}
          {config.discordBotEnabled && !preferences.discord.connected && <button className="button button-wide discord-button" onClick={connectDiscord} disabled={busy}><Bot size={18} />Add to Discord</button>}
          {preferences.discord.connected && (
            <div className="discord-connected">
              <div><span>Server</span><strong>{preferences.discord.guildName}</strong></div>
              <label>Alert channel<select value={preferences.discord.channelId || ''} onChange={(event) => selectChannel(event.target.value)}><option value="">Choose a channel…</option>{channels.map((channel) => <option value={channel.id} key={channel.id}>#{channel.name}</option>)}</select></label>
              <div className="command-row"><code>/spider latest</code><code>/spider status</code><code>/spider help</code></div>
            </div>
          )}
        </article>
      </div>
      <section className="dashboard-card compact-card">
        <div className="card-head"><div><h2>How bot delivery works</h2><p>No webhook URLs to copy or expose.</p></div><Webhook size={22} /></div>
        <ol className="mini-steps"><li><span>1</span>Click Add to Discord</li><li><span>2</span>Choose a server and alert channel</li><li><span>3</span>Press Spider posts each new mention</li></ol>
      </section>
    </>
  );
}

function Billing({ user, preferences, config, showToast }) {
  const [busy, setBusy] = useState(false);
  const query = new URLSearchParams(window.location.search);
  const [games, setGames] = useState(Number(query.get('games')) || preferences.billing.gameLimit || 1);
  const [currency, setCurrency] = useState(query.get('currency') || preferences.billing.currency || 'USD');
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [coupon, setCoupon] = useState('');
  const catalog = config.catalog || [];
  const crawler = catalog.find((item) => item.product_key === 'crawler');
  const quickCatch = catalog.find((item) => item.product_key === 'quick_catch');
  const addons = catalog.filter((item) => item.kind === 'addon' && item.active !== false);
  const price = (item) => Number(item?.[`price_${currency.toLowerCase()}_cents`] || 0);
  const addonTotal = addons.filter((item) => selectedAddons.includes(item.product_key)).reduce((total, item) => total + price(item) * (item.per_game ? games : 1), 0);
  const estimate = price(crawler) * games + addonTotal;
  const quickCatchPrice = price(quickCatch);
  async function checkout() {
    setBusy(true);
    try {
      const result = await api('/api/billing/subscription/checkout', { method: 'POST', body: JSON.stringify({ games, currency, addons: selectedAddons }) });
      window.location.assign(result.url);
    } catch (error) { showToast(error.message, true); setBusy(false); }
  }
  async function portal() {
    setBusy(true);
    try {
      const result = await api('/api/billing/portal', { method: 'POST' });
      window.location.assign(result.url);
    } catch (error) { showToast(error.message, true); setBusy(false); }
  }
  async function updateGames() {
    setBusy(true);
    try {
      const result = await api('/api/billing/quantity', { method: 'PATCH', body: JSON.stringify({ games }) });
      showToast(result.message);
    } catch (error) { showToast(error.message, true); }
    finally { setBusy(false); }
  }
  async function applyCoupon(event) {
    event.preventDefault(); setBusy(true);
    try {
      const result = await api('/api/billing/promotion-code', { method: 'POST', body: JSON.stringify({ code: coupon }) });
      setCoupon(''); showToast(result.message);
    } catch (error) { showToast(error.message, true); }
    finally { setBusy(false); }
  }
  return (
    <>
      <div className="app-title-row"><div><span className="eyebrow">Billing</span><h1>Choose how many webs to spin.</h1><p>Your free Web watch and CSV export remain available without a card.</p></div><label className="billing-currency">Currency<select value={currency} onChange={(event) => setCurrency(event.target.value)}>{['USD', 'EUR', 'GBP'].map((item) => <option key={item}>{item}</option>)}</select></label></div>
      {!config.billingEnabled && <div className="integration-note large">Billing is disabled in this environment. You can test the free product without Stripe.</div>}
      <div className="billing-layout">
        <article className="current-plan-card">
          <span className="eyebrow">Current plan</span><div className="plan-name"><h2>{preferences.billing.plan === 'scout' ? 'Crawler' : 'Free Watch'}</h2><StatusPill tone={preferences.billing.plan === 'scout' ? 'green' : 'neutral'}>{preferences.billing.complimentary ? 'Complimentary' : preferences.billing.subscriptionStatus || 'Free'}</StatusPill></div>
          <ul><li><FileText size={17} />Web source and CSV export</li><li><SourceGlyph source="youtube" size={17} />YouTube included with Crawler</li><li><SourceGlyph source="twitch" size={17} />Twitch included with Crawler</li></ul>
          {preferences.billing.plan === 'scout'
            ? preferences.billing.complimentary
              ? <div className="integration-note">This upgrade was granted by Press Spider. No Stripe payment is attached.</div>
              : <button className="button button-quiet button-wide" onClick={portal} disabled={busy || !config.billingEnabled}>Manage or cancel in Stripe</button>
            : <button className="button button-wide" onClick={checkout} disabled={busy || !config.billingEnabled}>Spin my web for {money(estimate, currency)}/month</button>}
        </article>
        <article className="bill-breakdown">
          {preferences.billing.complimentary ? <>
            <h2>Complimentary allowance</h2>
            <div><span>Games you can monitor</span><strong>{preferences.billing.gameLimit}</strong></div>
            <div><span>YouTube, Twitch and RSS</span><strong>Included</strong></div>
            <div className="bill-total"><span>Monthly charge</span><strong>{money(0, currency)}</strong></div>
            <p>An administrator can change this allowance for you. No Stripe subscription or payment is required.</p>
          </> : <>
            <h2>Build your monthly Crawler</h2>
            <label className="game-count-control"><span>Games to monitor</span><select value={games} onChange={(event) => setGames(Number(event.target.value))}>{Array.from({ length: 10 }, (_, index) => index + 1).map((count) => <option value={count} key={count}>{count}</option>)}</select></label>
            <div><span>Crawler × {games}</span><strong>{money(price(crawler) * games, currency)}</strong></div>
            {addons.map((addon) => <label className="billing-addon" key={addon.product_key}><input type="checkbox" checked={selectedAddons.includes(addon.product_key)} onChange={() => setSelectedAddons((items) => items.includes(addon.product_key) ? items.filter((key) => key !== addon.product_key) : [...items, addon.product_key])} /><span>{addon.name}</span><strong>{money(price(addon) * (addon.per_game ? games : 1), currency)}</strong></label>)}
            <div className="bill-total"><span>Estimated monthly total</span><strong>{money(estimate, currency)}</strong></div>
            <p>YouTube and Twitch are included. Stripe shows taxes and the final amount before payment. Cancel anytime.</p>
            {preferences.billing.plan === 'scout' && <button className="button button-wide" onClick={updateGames} disabled={busy || games === preferences.billing.gameLimit}>Update to {games} {games === 1 ? 'game' : 'games'}</button>}
          </>}
        </article>
      </div>
      {preferences.billing.plan === 'scout' && !preferences.billing.complimentary && <form className="coupon-card" onSubmit={applyCoupon}><div><h2>Have a promotion code?</h2><p>Codes are created and restricted in Stripe. Valid discounts apply to your subscription.</p></div><label>Promotion code<input value={coupon} onChange={(event) => setCoupon(event.target.value.replace(/[^A-Za-z0-9]/g, ''))} required placeholder="FRIEND20" /></label><button className="button button-quiet" disabled={busy}>Apply code</button></form>}
      <section className="quick-catch-banner"><span><Sparkles size={25} /></span><div><h2>Need a launch burst, not continuous monitoring?</h2><p>Quick Catch runs for 7 days from {money(quickCatchPrice, currency)} per game. Results remain available for {config.quickCatchRetentionDays || 90} days, then are automatically deleted. Export the CSV any time before expiry.</p></div></section>
    </>
  );
}

function AddWatchModal({ open, onClose, onCreated, showToast }) {
  const [busy, setBusy] = useState(false);
  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    const form = new FormData(event.currentTarget);
    try {
      const result = await api('/api/games', {
        method: 'POST',
        body: JSON.stringify({
          title: form.get('title'),
          aliases: String(form.get('aliases') || '').split(/[\n,]/).map((item) => item.trim()).filter(Boolean),
          stores: [
            { store: 'steam', url: form.get('steamUrl') },
            { store: 'epic', url: form.get('epicUrl') },
            { store: 'gog', url: form.get('gogUrl') },
          ].filter((item) => item.url),
        }),
      });
      event.currentTarget.reset();
      onCreated(result.game);
      showToast(`${result.game.title} is now crawling. The first Web check is queued.`);
    } catch (error) { showToast(error.message, true); }
    finally { setBusy(false); }
  }
  return (
    <Modal open={open} onClose={onClose} title="Spin a new watch">
      <form className="stack-form" onSubmit={submit}>
        <label>Game title<input name="title" required minLength="2" maxLength="120" placeholder="Moonlake" autoFocus /></label>
        <label>Alternate names <span>optional</span><textarea name="aliases" rows="3" placeholder="Moon Lake&#10;Moonlake Game" /></label>
        <fieldset className="store-links"><legend>Store pages <span>optional, add any that apply</span></legend><label>Steam<input name="steamUrl" type="url" placeholder="https://store.steampowered.com/app/…" /></label><label>Epic Games Store<input name="epicUrl" type="url" placeholder="https://store.epicgames.com/en-US/p/…" /></label><label>GOG<input name="gogUrl" type="url" placeholder="https://www.gog.com/en/game/…" /></label></fieldset>
        <div className="form-note"><SourceGlyph source="press" size={17} /><span>Web starts free. Your account source preferences apply to this watch.</span></div>
        <button className="button button-wide" disabled={busy}>{busy ? 'Spinning…' : 'Start crawling'}<Radar size={17} /></button>
      </form>
    </Modal>
  );
}

function Stat({ value, label, accent }) {
  return <article className={`stat-card ${accent ? 'accent' : ''}`}><strong>{value}</strong><span>{label}</span></article>;
}

function Empty({ title, body, action }) {
  return <div className="empty-state"><span><Radar size={22} /></span><div><strong>{title}</strong><p>{body}</p>{action}</div></div>;
}

function MobileTabs({ tabs, tab, setTab }) {
  return <nav className="mobile-tabs">{tabs.map(([key, Icon, label]) => <button key={key} className={tab === key ? 'active' : ''} onClick={() => setTab(key)}><Icon size={19} /><span>{label}</span></button>)}</nav>;
}
